import { Button, Dialog, Input } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreerExposition } from '../../api/expositions.js';
import { useCreerLieu, type LieuPresente } from '../../api/lieux.js';
import { galerieALaction } from '../../auth/sujet.js';
import type { GalerieSession } from '../../api/types.js';
import { ChampsManquants } from '../ChampsManquants.js';
import { Field } from '../Field.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { useFermetureGardee } from '../useFermetureGardee.js';
import styles from './CreationStand.module.css';

/**
 * Ce que le « + » du mode foire offre, selon les droits de la session : les
 * mêmes que le reste de la régie (lieux : `settings:locations`, foires :
 * `exhibitions:write`, œuvres au stand : le lot de localisation, qui exige
 * `artworks:write`). Mêmes règles que sur iOS.
 */
export const peutCreerStand = (galerie: GalerieSession): boolean =>
  galerieALaction(galerie, 'settings:locations');

/** Une foire porte son stand : les deux droits sont nécessaires. */
export const peutCreerFoire = (galerie: GalerieSession): boolean =>
  peutCreerStand(galerie) && galerieALaction(galerie, 'exhibitions:write');

export const peutAjouterDesOeuvres = (galerie: GalerieSession): boolean =>
  galerieALaction(galerie, 'artworks:write');

interface DialogueStandProps {
  galerieSlug: string;
  onOpenChange: (open: boolean) => void;
  /** Le stand existe : le mode foire le montre aussitôt. */
  onStandCree: (stand: LieuPresente) => void;
}

/** Un stand : un lieu de type `foire`, dont les œuvres localisées forment le stand. */
export function NouveauStandDialog({
  galerieSlug,
  onOpenChange,
  onStandCree,
}: DialogueStandProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [nom, setNom] = useState('');
  const formulaire = useErreurFormulaire();
  const creer = useCreerLieu(galerieSlug);
  const garde = useFermetureGardee(nom.trim().length > 0, onOpenChange);
  const manques = nom.trim() ? [] : [t('regie:foire.creation.nomStand')];

  async function soumettre(): Promise<void> {
    formulaire.reinitialiser();
    try {
      const { lieu } = await creer.mutateAsync({ nom: nom.trim(), type: 'foire' });
      onOpenChange(false);
      onStandCree(lieu);
    } catch (error) {
      formulaire.appliquer(error, ['nom']);
    }
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={garde.onOpenChange}
        title={t('regie:foire.creation.titreStand')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => garde.onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void soumettre()}
              loading={creer.isPending}
              disabled={manques.length > 0}
            >
              {t('regie:foire.creation.creer')}
            </Button>
          </>
        }
      >
        <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
          <Field
            label={t('regie:foire.creation.nomStand')}
            htmlFor="nouveau-stand-nom"
            error={formulaire.messageChamp('nom')}
          >
            <Input
              id="nouveau-stand-nom"
              required
              maxLength={200}
              value={nom}
              onChange={(event) => setNom(event.target.value)}
            />
          </Field>
          <p className={styles.aide}>{t('regie:foire.creation.aideStand')}</p>
          <ChampsManquants champs={manques} />
          {formulaire.erreur ? (
            <p className={styles.erreur} role="alert">
              {formulaire.erreur}
            </p>
          ) : null}
        </form>
      </Dialog>
      {garde.confirmation}
    </>
  );
}

/**
 * Une foire : une exposition de type `foire` posée sur son stand. Le stand est
 * créé d'abord ; si la foire échoue ensuite, il est gardé et réessayer ne le
 * crée pas une seconde fois.
 */
export function NouvelleFoireDialog({
  galerieSlug,
  onOpenChange,
  onStandCree,
}: DialogueStandProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [titre, setTitre] = useState('');
  const [nomStand, setNomStand] = useState('');
  const [debutLe, setDebutLe] = useState('');
  const [finLe, setFinLe] = useState('');
  const [stand, setStand] = useState<LieuPresente | null>(null);
  const [enCours, setEnCours] = useState(false);
  const formulaire = useErreurFormulaire();
  const creerLieu = useCreerLieu(galerieSlug);
  const creerExposition = useCreerExposition(galerieSlug);

  const finAvantDebut = debutLe !== '' && finLe !== '' && finLe < debutLe;
  const manques = [
    titre.trim() ? null : t('regie:expositions.nouvelle.titreChamp'),
    debutLe ? null : t('regie:expositions.nouvelle.debutChamp'),
    finLe ? null : t('regie:expositions.nouvelle.finChamp'),
  ].filter((champ): champ is string => champ !== null);
  const garde = useFermetureGardee(
    stand === null && [titre, nomStand, debutLe, finLe].some((valeur) => valeur.trim().length > 0),
    onOpenChange,
  );

  async function soumettre(): Promise<void> {
    formulaire.reinitialiser();
    setEnCours(true);
    try {
      let lieu = stand;
      if (!lieu) {
        const cree = await creerLieu.mutateAsync({
          nom: nomStand.trim() || titre.trim(),
          type: 'foire',
        });
        lieu = cree.lieu;
        setStand(lieu);
        onStandCree(lieu);
      }
      await creerExposition.mutateAsync({
        type: 'foire',
        titre: { fr: titre.trim() },
        debutLe,
        finLe,
        lieuId: lieu.id,
      });
      onOpenChange(false);
    } catch (error) {
      formulaire.appliquer(error, ['titre', 'debutLe', 'finLe']);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={garde.onOpenChange}
        title={t('regie:foire.creation.titreFoire')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => garde.onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void soumettre()}
              loading={enCours}
              disabled={manques.length > 0 || finAvantDebut}
            >
              {t('regie:foire.creation.creer')}
            </Button>
          </>
        }
      >
        <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
          <Field
            label={t('regie:foire.creation.titreFoireChamp')}
            htmlFor="nouvelle-foire-titre"
            error={formulaire.messageChamp('titre')}
          >
            <Input
              id="nouvelle-foire-titre"
              required
              maxLength={300}
              value={titre}
              onChange={(event) => setTitre(event.target.value)}
            />
          </Field>
          <Field label={t('regie:foire.creation.nomStandDefaut')} htmlFor="nouvelle-foire-stand">
            <Input
              id="nouvelle-foire-stand"
              maxLength={200}
              value={nomStand}
              disabled={stand !== null}
              onChange={(event) => setNomStand(event.target.value)}
            />
          </Field>
          <p className={styles.aide}>{t('regie:foire.creation.aideFoire')}</p>
          <div className={styles.periode}>
            <Field
              label={t('regie:expositions.nouvelle.debutChamp')}
              htmlFor="nouvelle-foire-debut"
              error={formulaire.messageChamp('debutLe')}
            >
              <Input
                id="nouvelle-foire-debut"
                type="date"
                required
                value={debutLe}
                onChange={(event) => setDebutLe(event.target.value)}
              />
            </Field>
            <Field
              label={t('regie:expositions.nouvelle.finChamp')}
              htmlFor="nouvelle-foire-fin"
              error={
                finAvantDebut
                  ? t('regie:validation.finAvantDebut')
                  : formulaire.messageChamp('finLe')
              }
            >
              <Input
                id="nouvelle-foire-fin"
                type="date"
                required
                value={finLe}
                onChange={(event) => setFinLe(event.target.value)}
              />
            </Field>
          </div>
          <ChampsManquants champs={finAvantDebut ? [] : manques} />
          {formulaire.erreur ? (
            <p className={styles.erreur} role="alert">
              {stand ? `${t('regie:foire.creation.standCreeSansFoire')} ` : ''}
              {formulaire.erreur}
            </p>
          ) : null}
        </form>
      </Dialog>
      {garde.confirmation}
    </>
  );
}
